import { useQuery } from "@tanstack/react-query";
import api from "../../../services/api";
import { getFactoryFacilities } from "../../../services/kwsService";
import type {
  PaymentQueueParams,
  PaymentQueueResponse,
  PaymentDetailResponse,
  PaymentSummaryParams,
  PaymentSummaryResponse,
} from "../types/payment";
import { paymentKeys, facilityKeys, PAYMENT_BASE_URL } from "./queryKeys";

// ---------------------------------------------------------------------------
// Raw async functions
// ---------------------------------------------------------------------------

// Array params serialised as comma-joined values (e.g. ?request_status=SUBMITTED,UNDER_REVIEW).
// vendor_facility_id uses the same convention: vendor_facility_id=28,179 per the API contract.
const COMMA_JOINED_PARAMS = new Set(["payment_type", "request_status", "vendor_facility_id"]);

function buildSearchParams(params: Record<string, unknown>): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    if (Array.isArray(value)) {
      const clean = value.filter((v) => v != null && v !== "");
      if (!clean.length) continue;
      if (COMMA_JOINED_PARAMS.has(key)) {
        sp.set(key, clean.join(","));
      } else {
        clean.forEach((v) => sp.append(key, String(v)));
      }
    } else {
      sp.set(key, String(value));
    }
  }
  return sp;
}

export async function getPaymentQueue(
  params: PaymentQueueParams,
): Promise<PaymentQueueResponse> {
  const sp = buildSearchParams(params as Record<string, unknown>);
  return api.get(`${PAYMENT_BASE_URL}/queue?${sp.toString()}`);
}

export async function getPaymentSummary(
  params: PaymentSummaryParams,
): Promise<PaymentSummaryResponse> {
  const sp = buildSearchParams(params as Record<string, unknown>);
  return api.get(`${PAYMENT_BASE_URL}/queue/summary?${sp.toString()}`);
}

export async function getPaymentDetail(id: number): Promise<PaymentDetailResponse> {
  return api.get(`${PAYMENT_BASE_URL}/${id}`);
}

// ---------------------------------------------------------------------------
// Query factories
// ---------------------------------------------------------------------------

export function paymentQueueQuery(params: PaymentQueueParams) {
  return {
    queryKey: paymentKeys.list(params),
    queryFn: () => getPaymentQueue(params),
    keepPreviousData: true,
  };
}

export function paymentDetailQuery(id: number) {
  return {
    queryKey: paymentKeys.detail(id),
    queryFn: () => getPaymentDetail(id),
    enabled: !!id,
  };
}

export function paymentSummaryQuery(params: PaymentSummaryParams) {
  return {
    queryKey: paymentKeys.summary(params),
    queryFn: () => getPaymentSummary(params),
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function usePaymentQueue(params: PaymentQueueParams, options?: { enabled?: boolean }) {
  return useQuery({ ...paymentQueueQuery(params), ...options });
}

export function usePaymentDetail(id: number) {
  return useQuery(paymentDetailQuery(id));
}

export function usePaymentSummary(params: PaymentSummaryParams, options?: { enabled?: boolean }) {
  return useQuery({ ...paymentSummaryQuery(params), ...options });
}

// ---------------------------------------------------------------------------
// Factory → facility ID resolution
// ---------------------------------------------------------------------------

/**
 * Resolves a factory code (e.g. "FCT_123") to the numeric vendor_facility_id
 * array used by the payments API.
 *
 * Disabled when factoryCode is undefined/empty/"all" — returns undefined data
 * rather than an empty array so callers can distinguish "not yet resolved"
 * from "resolved to empty".
 */
export function useFactoryFacilityIds(factoryCode: string | undefined) {
  return useQuery<number[]>({
    queryKey: facilityKeys.ids(factoryCode ?? "__none__"),
    queryFn: async () => {
      const ids = await getFactoryFacilities(factoryCode!);
      // Guard: API can return null for factories with no VIP facility records.
      // Filter out null/zero/negative IDs so callers always receive a clean number[].
      return (ids ?? []).filter((id: number) => id > 0);
    },
    enabled: !!factoryCode && factoryCode !== "all",
    staleTime: 5 * 60 * 1000, // facility mapping is stable; don't re-fetch every render
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Ready to Batch — batchable requests grouped by vendor facility
// ---------------------------------------------------------------------------

// Raw shape returned by GET /batchable
interface BatchableApiItem {
  vendor_facility_id: number;
  vendor_facility_name: string;
  batchable_count: number;
  total_amount: number;
}

export interface BatchFacilityGroup {
  facilityId: number;
  facilityName: string;
  count: number;
  totalAmount: number;
}

async function getBatchable(): Promise<BatchFacilityGroup[]> {
  const res: BatchableApiItem[] | null = await api.get(`${PAYMENT_BASE_URL}/batchable`);
  return (res ?? []).map((item) => ({
    facilityId: item.vendor_facility_id,
    facilityName: item.vendor_facility_name || "—",
    count: item.batchable_count,
    totalAmount: item.total_amount,
  }));
}

export function batchableQuery() {
  return {
    queryKey: paymentKeys.batchable(),
    queryFn: getBatchable,
  };
}

export function useBatchGroups() {
  return useQuery(batchableQuery());
}

// ---------------------------------------------------------------------------
// UTR upload template CSV download
// ---------------------------------------------------------------------------

export interface TemplateDownloadResult {
  blob: Blob;
  truncated: boolean;
  filename: string;
}

/**
 * Downloads the UTR upload template CSV for the given filter context.
 *
 * Uses api.exec (the api singleton) with a custom transformResponse so we
 * capture both the blob and the response headers before the response
 * interceptor strips them. Auth is injected by the existing request
 * interceptor — no separate auth import needed.
 *
 * request_status is intentionally omitted: the BE defaults to APPROVED,
 * the only status that accepts UTRs. Including other statuses would produce
 * rows guaranteed to fail on upload.
 *
 * X-Truncated MUST be surfaced to the user — a silent truncation means rows
 * go unpaid.
 */
export async function downloadPaymentTemplate(
  params: PaymentSummaryParams,
): Promise<TemplateDownloadResult> {
  const sp = buildSearchParams(params as Record<string, unknown>);

  // transformResponse also runs on error responses in axios 0.27, so a non-2xx
  // JSON error body is accessible as err.response.data.blob. We try to extract
  // the BE's message before rethrowing so the user sees "export cap exceeded"
  // rather than axios's generic "Request failed with status code 400".
  let blob: unknown, headers: Record<string, string>;
  try {
    ({ blob, headers } = await api.exec({
      url: `${PAYMENT_BASE_URL}/queue/utr-template?${sp.toString()}`,
      method: "get",
      responseType: "blob",
      // transformResponse runs before the response interceptor strips headers,
      // so we bundle blob + headers into a single object that the interceptor
      // passes through as response.data.
      transformResponse: [
        (data: unknown, hdrs: Record<string, string>) => ({ blob: data, headers: hdrs }),
      ],
    }));
  } catch (err: any) {
    const errBlob = err?.response?.data?.blob;
    if (errBlob?.text) {
      let msg: string | undefined;
      try { msg = JSON.parse(await errBlob.text())?.message; } catch { /* not JSON */ }
      if (msg) throw new Error(msg);
    }
    throw err;
  }

  // BE may return errors as a JSON string even on this text/csv endpoint (2xx with JSON body)
  const contentType: string = (headers as Record<string, string>)["content-type"] ?? "";
  if (contentType.includes("application/json")) {
    const text: string = await (blob as Blob).text();
    let message = "Download failed";
    try { message = JSON.parse(text)?.message ?? message; } catch { /* ignore */ }
    throw new Error(message);
  }

  // TODO: confirm with BE/platform that the gateway exposes these headers before merge:
  //   Access-Control-Expose-Headers: X-Truncated, Content-Disposition
  // X-Truncated and Content-Disposition are non-safelisted CORS response headers — without
  // the above gateway config, x-truncated is undefined → truncated stays false → silent data
  // loss (rows go unpaid with no warning), and content-disposition is "" → filename falls back
  // to the date-stamped default.
  // Until confirmed, the only fallback is the zero-row check in PaymentList.tsx
  // handleDownloadTemplate — it catches a completely empty export, but NOT a partial
  // one (a capped 5k-of-8k export looks identical to a complete one without this header).
  const truncated = String(headers["x-truncated"]).toLowerCase() === "true";
  const disposition: string = headers["content-disposition"] ?? "";
  const filenameMatch = disposition.match(/filename="([^"]+)"/);
  const filename =
    filenameMatch?.[1] ??
    `utr-upload-${new Date().toISOString().slice(0, 10)}.csv`;

  return { blob: blob as Blob, truncated, filename };
}
